import React, { useCallback, useMemo } from 'react';

import { Grid2 as Grid } from '@mui/material';

import { FixedColumnSlider, ResponsiveGrid, useSliderViewport } from '@mewa/ui';

import {
  PageFindResultData,
  PressReleasesSearchResultMeta,
} from '../../components/search/types';
import { Article } from '../press/overview';

export const SearchResultPressReleases = React.memo(
  ({
    results,
    expanded,
    ...context
  }: {
    results: PageFindResultData<PressReleasesSearchResultMeta>[];
    expanded: boolean;
    locale: string;
  }) => {
    const sliderViewport = useSliderViewport();

    const renderSlide = useCallback(
      (
        result: PageFindResultData<PressReleasesSearchResultMeta>,
        idx: number,
      ) => <PressRelease result={result} key={idx} {...context} />,
      [context],
    );

    const sliderView = useMemo(
      () => (
        <FixedColumnSlider
          slides={results}
          viewport={sliderViewport}
          renderSlide={renderSlide}
          withCardSpacing={true}
        />
      ),
      [results, sliderViewport, renderSlide],
    );

    const gridView = useMemo(
      () => (
        <ResponsiveGrid>
          {results.map((result, index) => (
            <Grid
              key={index}
              size={{
                xs: 12,
                sm: 6,
                md: 4,
              }}
            >
              <PressRelease result={result} {...context} />
            </Grid>
          ))}
        </ResponsiveGrid>
      ),
      [results, context],
    );

    return expanded ? gridView : sliderView;
  },
);

const PressRelease = ({
  result,
  locale,
}: {
  result: PageFindResultData<PressReleasesSearchResultMeta>;
  locale: string;
}) => (
  <Article
    key={result.url}
    date={result.meta.date}
    title={result.meta.title}
    mediaDownloads={[
      {
        mediaToDownload: {
          // We use static values for width and height, because we do not know the exact size.
          // Images are being rendered correctly by the Article component.
          width: 1920,
          height: 1080,
          fields: { assetUrl: result.meta.image || '' },
          extension: 'jpg', // can be static
          mime: 'image/jpg', // can be static, must start with `image/`
          name: result.meta.title,
        },
        thumbnailImage: null,
        thumbnailAlternativeText: '',
      },
    ]}
    topics={[]}
    href={result.url}
    locale={locale}
    isFirstArticle={false}
  />
);
